import { useApolloClient } from "@apollo/client/react";
import { get } from "lodash";
import { useCallback, useMemo } from "react";

import {
  createDesign as createDesignMutation,
  deleteDesign as deleteDesignMutation,
  getDesign as getDesignQuery,
  designTypes as designTypesQuery,
  designWorkflowStages as designWorkflowStagesQuery,
  listDesigns as listDesignsQuery,
  updateDesign as updateDesignMutation,
} from "./designs.graphql";
import {
  createDesignOption as createDesignOptionMutation,
  deleteDesignOption as deleteDesignOptionMutation,
  updateDesignOption as updateDesignOptionMutation,
} from "./overlay/designOption.graphql";
import { buildDesignMutationInput } from "./utils";

export const useDesignsRepository = () => {
  const client = useApolloClient();

  const listDesigns = useCallback(async () => {
    const { data } = await client.query({
      query: listDesignsQuery,
      fetchPolicy: "network-only",
    });
    return get(data, "designs", []);
  }, [client]);

  const getDesign = useCallback(
    async ({ id }) => {
      const { data } = await client.query({
        query: getDesignQuery,
        variables: { id },
        fetchPolicy: "network-only",
      });
      return get(data, "design", null);
    },
    [client]
  );

  const createDesign = useCallback(
    async ({ input }) => {
      const { data } = await client.mutate({
        mutation: createDesignMutation,
        variables: { input: buildDesignMutationInput(input) },
      });
      return get(data, "design", null);
    },
    [client]
  );

  const updateDesign = useCallback(
    async ({ id, input }) => {
      const { data } = await client.mutate({
        mutation: updateDesignMutation,
        variables: { id, input: buildDesignMutationInput(input) },
      });
      return get(data, "design", null);
    },
    [client]
  );

  const deleteDesign = useCallback(
    async ({ id }) => {
      const { data } = await client.mutate({
        mutation: deleteDesignMutation,
        variables: { id },
      });
      return get(data, "deleteDesign.deleted", false);
    },
    [client]
  );

  const getWorkflowStages = useCallback(async () => {
    const { data } = await client.query({
      query: designWorkflowStagesQuery,
    });
    return get(data, "designWorkflowStages", []);
  }, [client]);

  const getDesignTypes = useCallback(async () => {
    const { data } = await client.query({
      query: designTypesQuery,
    });
    return get(data, "designTypes", []);
  }, [client]);

  const createDesignOption = useCallback(
    async ({ id, name }) => {
      const { data } = await client.mutate({
        mutation: createDesignOptionMutation,
        variables: { id, input: { name } },
      });
      return get(data, "option", null);
    },
    [client]
  );

  const renameDesignOption = useCallback(
    async ({ id, optionId, name }) => {
      const { data } = await client.mutate({
        mutation: updateDesignOptionMutation,
        variables: { id, optionId, input: { name } },
      });
      return get(data, "option", null);
    },
    [client]
  );

  const deleteDesignOption = useCallback(
    async ({ id, optionId }) => {
      const { data } = await client.mutate({
        mutation: deleteDesignOptionMutation,
        variables: { id, optionId },
      });
      return get(data, "deleteDesignOption.deleted", false);
    },
    [client]
  );

  return useMemo(
    () => ({
      listDesigns,
      getDesign,
      createDesign,
      updateDesign,
      deleteDesign,
      getWorkflowStages,
      getDesignTypes,
      createDesignOption,
      renameDesignOption,
      deleteDesignOption,
    }),
    [
      listDesigns,
      getDesign,
      createDesign,
      updateDesign,
      deleteDesign,
      getWorkflowStages,
      getDesignTypes,
      createDesignOption,
      renameDesignOption,
      deleteDesignOption,
    ]
  );
};

export default useDesignsRepository;
